import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import {
  ScanEventsService,
  SortScansResponse,
  GetSortScansParams,
  ExportSortScansParams,
} from '@app/shared/services/scan-events.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { DateTime } from 'luxon';
import { ScanEventsAirportCodes } from '@app/shared/constants';
import { Subject } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-sort-scans-reports',
  templateUrl: './sort-scans-reports.component.html',
  styleUrls: ['./sort-scans-reports.component.scss'],
})
export class SortScansReportsComponent implements OnInit, OnDestroy {
  @Input() user: AdminUser;

  public isAppleExpressUser: boolean = false;
  public canExportCsv: boolean = false;
  public loading: boolean = false;
  public hasError: boolean = false;
  public errorMessage: string = '';
  // Export loading state
  public exporting: boolean = false;

  // Fallback location codes from constants
  private fallbackLocationCodes = ScanEventsAirportCodes;

  // Date range (default last 6 days)
  public daterange = {
    start: DateTime.now().minus({ days: 6 }).startOf('day').toJSDate(),
    end: DateTime.now().endOf('day').toJSDate(),
  };

  public range = new FormGroup({
    start: new FormControl<Date | null>(this.daterange.start),
    end: new FormControl<Date | null>(this.daterange.end),
  });

  // Date picker constraints: max 6 days range, no future dates
  private readonly today: Date = DateTime.now().endOf('day').toJSDate();
  public maxDate: Date = this.today;

  // Filter selections
  public selectedLocations: string[] = [];

  // Response data
  public responseData: SortScansResponse | null = null;

  // Table columns (dynamically generated from date range)
  public dateColumns: string[] = [];

  // Subscription management
  private destroy$ = new Subject<void>();

  constructor(
    private scanEventsService: ScanEventsService,
    private bannerNotificationsService: BannerNotificationsService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.isAppleExpressUser = this.userService.hasAuthScope(this.user, [
      AdminScopes.VIEW_SCAN_EVENTS_SORTS_APPLEEXPRESS,
    ]);
    this.canExportCsv = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_SCAN_EVENTS_SORTS]);
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Getter for displayed columns (fixes arrow function in template).
   * Includes date columns plus a total column at the end.
   */
  public get displayedColumns(): string[] {
    return ['location'].concat(this.dateColumns.map((_, i) => 'date_' + i)).concat(['total_scanned']);
  }

  /**
   * Getter for footer columns.
   * Includes date footer columns plus a total footer column at the end.
   */
  public get footerColumns(): string[] {
    return ['location-footer']
      .concat(this.dateColumns.map((_, i) => 'date_' + i + '_footer'))
      .concat(['total_scanned_footer']);
  }

  /**
   * Getter for location filter options.
   * Uses available locations from response data, fallback to constants.
   */
  public get locationOptions(): string[] {
    if (this.responseData?.availableLocations?.length) {
      return this.responseData.availableLocations;
    }
    return this.fallbackLocationCodes;
  }

  /**
   * Get display timezone from browser.
   */
  private getDisplayTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
    } catch {
      return 'America/Toronto';
    }
  }

  /**
   * Load sort scans data from API.
   */
  public loadData(): void {
    if (!this.range.value.start || !this.range.value.end) {
      return;
    }

    this.loading = true;
    this.hasError = false;

    const localTimezone = this.getDisplayTimezone();
    const params: GetSortScansParams = {
      startDate: DateTime.fromJSDate(this.range.value.start).startOf('day').toUTC().toISO(),
      endDate: DateTime.fromJSDate(this.range.value.end).endOf('day').toUTC().toISO(),
      localTimezone,
      locations: this.selectedLocations.length > 0 ? this.selectedLocations : undefined,
    };

    this.scanEventsService.getSortScans(params).subscribe({
      next: (response: SortScansResponse) => {
        this.responseData = response;
        this.generateDateColumns();
        this.loading = false;
      },
      error: (error) => {
        this.hasError = true;
        this.errorMessage = typeof error === 'string' ? error : 'Failed to load sort scans data';
        this.bannerNotificationsService.error(this.errorMessage);
        this.loading = false;
      },
    });
  }

  /**
   * Handle start date selection in the date range picker.
   * Constrains maxDate to start + 6 days (or today, whichever is earlier)
   * so the end date selection is restricted to a 6-day window.
   */
  public onStartDateChange(event: any): void {
    if (event.value) {
      const startPlus6 = DateTime.fromJSDate(event.value).plus({ days: 6 }).endOf('day').toJSDate();
      this.maxDate = startPlus6 < this.today ? startPlus6 : this.today;

      // If existing end date exceeds new max, clear it
      const endDate = this.range.get('end')?.value;
      if (endDate && endDate > this.maxDate) {
        this.range.get('end')?.setValue(null);
      }
    }
  }

  /**
   * Handle date range picker opened.
   * Resets maxDate to today when both dates are set, so the user
   * can freely pick any past date as a new start date.
   */
  public onPickerOpened(): void {
    const startDate = this.range.get('start')?.value;
    const endDate = this.range.get('end')?.value;

    if (startDate && endDate) {
      this.maxDate = this.today;
    } else if (startDate && !endDate) {
      const startPlus6 = DateTime.fromJSDate(startDate).plus({ days: 6 }).endOf('day').toJSDate();
      this.maxDate = startPlus6 < this.today ? startPlus6 : this.today;
    } else {
      this.maxDate = this.today;
    }
  }

  /**
   * Generate date column headers from response data.
   * Backend now returns dates normalized to user's local timezone, so no conversion needed.
   */
  private generateDateColumns(): void {
    if (this.responseData?.dailyTotalData?.length) {
      // Backend returns dates already in local timezone format (YYYY-MM-DD)
      // Just format for display without timezone conversion
      this.dateColumns = this.responseData.dailyTotalData.map((d) => DateTime.fromISO(d.datetime).toFormat('MMM dd'));
    } else {
      this.dateColumns = [];
    }
  }

  /**
   * Get formatted date header with day name (e.g., "Mon, Jan 5").
   */
  public getFormattedDateHeader(dateIndex: number): string {
    if (this.responseData?.dailyTotalData?.[dateIndex]) {
      const dt = DateTime.fromISO(this.responseData.dailyTotalData[dateIndex].datetime);
      return dt.toFormat('ccc, MMM d');
    }
    return '';
  }

  /**
   * Export current data to CSV via backend API.
   */
  public exportToCsv(): void {
    if (!this.range.value.start || !this.range.value.end) {
      return;
    }

    this.exporting = true;

    const localTimezone = this.getDisplayTimezone();
    const params: ExportSortScansParams = {
      startDate: DateTime.fromJSDate(this.range.value.start).startOf('day').toUTC().toISO(),
      endDate: DateTime.fromJSDate(this.range.value.end).endOf('day').toUTC().toISO(),
      localTimezone,
      locations: this.selectedLocations.length > 0 ? this.selectedLocations : undefined,
    };

    this.scanEventsService.exportSortScansCsv(params).subscribe({
      next: (blob: Blob) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `sort-scans-${DateTime.fromISO(params.startDate, { zone: 'UTC' })
            .setZone(localTimezone)
            .toFormat('yyyy-MM-dd')}-${DateTime.fromISO(params.endDate, { zone: 'UTC' })
            .setZone(localTimezone)
            .toFormat('yyyy-MM-dd')}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: (error) => {
        this.bannerNotificationsService.error(typeof error === 'string' ? error : 'Failed to export sort scans data');
        this.exporting = false;
      },
    });
  }

  /**
   * Get outbound count for a location on a specific date.
   */
  public getOutboundCount(locationData: any, dateIndex: number): number {
    if (locationData?.aggregatedData?.[dateIndex]) {
      return locationData.aggregatedData[dateIndex].outboundSortingScansCount;
    }
    return 0;
  }

  /**
   * Get inbound count for a location on a specific date (currently mocked as 0).
   */
  public getInboundCount(locationData: any, dateIndex: number): number {
    if (locationData?.aggregatedData?.[dateIndex]) {
      return locationData.aggregatedData[dateIndex].inboundSortingScansCount;
    }
    return 0;
  }

  /**
   * Get daily total outbound for a specific date index.
   */
  public getDailyTotalOutbound(dateIndex: number): number {
    if (this.responseData?.dailyTotalData?.[dateIndex]) {
      return this.responseData.dailyTotalData[dateIndex].totalOutboundSortingScans;
    }
    return 0;
  }

  /**
   * Get daily total inbound for a specific date index (currently mocked as 0).
   */
  public getDailyTotalInbound(dateIndex: number): number {
    if (this.responseData?.dailyTotalData?.[dateIndex]) {
      return this.responseData.dailyTotalData[dateIndex].totalInboundSortingScans;
    }
    return 0;
  }

  /**
   * Get grand total outbound across all dates.
   * Uses totalFirstMileScans from response for consistency with summary cards.
   */
  public getGrandTotalOutbound(): number {
    return this.responseData?.totalFirstMileScans || 0;
  }

  /**
   * Get grand total inbound across all dates.
   * Uses totalLastMileScans from response for consistency with summary cards.
   */
  public getGrandTotalInbound(): number {
    return this.responseData?.totalLastMileScans || 0;
  }

  /**
   * Format number for display.
   */
  public formatNumber(value: number): string {
    return value.toLocaleString();
  }
}
